import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import type {LegalPage} from 'types/payload-types';

/**
 * The props for the `LegalFooter` component.
 */
interface ComponentProps {
    /** The legal pages to render as links, already sorted (see `getLegalPages`). */
    legalPages: LegalPage[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'LegalFooter'
     */
    testId?: string;
}

/**
 * A compact row of legal page links (Impressum, Datenschutz, AGB, etc.), rendered at the
 * end of every page's content via `AppLayout`. Makes the legal pages reachable from anywhere
 * in the app instead of only from the settings page.
 *
 * @param props            The component props.
 * @param props.legalPages The legal pages to render as links.
 * @param props.testId     An optional test ID used for automation selectors. Defaults to `'LegalFooter'`.
 * @returns A JSX element rendering the LegalFooter, or `null` if there are no legal pages.
 *
 * @example
 * ```tsx
 * <LegalFooter legalPages={legalPages} />
 * ```
 */
const LegalFooter = ({legalPages, testId = 'LegalFooter'}: ComponentProps) => {
    if (legalPages.length === 0) return null;

    return (
        <Nav aria-label="Rechtliches" data-cy={testId}>
            {Array.from(legalPages, page => (
                <LegalLink key={page.slug} data-cy={`${testId}-link`} href={`/legal/${page.slug}`}>
                    {page.title}
                </LegalLink>
            ))}
        </Nav>
    );
};

LegalFooter.displayName = 'LegalFooter';

export {LegalFooter};

// ── Styled ──────────────────────────────────────────────────

const Nav = styled.nav`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(1)} ${spacing(3)};
    justify-content: center;
    padding: ${spacing(6)} ${spacing(4)};
`;

const LegalLink = styled(Link)`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;